import { useState, type FormEvent } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Trash2 } from "lucide-react";
import { CHAIN_PROFILES, de, type ChainKey } from "@ainkauf/core";
import { BottomNav } from "@/components/BottomNav";
import { PushSchalter } from "@/components/PushSchalter";
import { SpaisekammerCard } from "@/components/SpaisekammerCard";
import { InviteCard } from "@/components/InviteCard";
import { SyncBadge } from "@/components/SyncBadge";
import { alleNeuLaden, allMembers, allStores, createStore, deleteStore, selbstauskunft, syncStatus, type HaengendeAenderung } from "@/db/repository";
import { signOut } from "@/lib/auth";
import { supabaseConfigured } from "@/lib/supabase";

/** Einstellungen: wer gehört dazu, wo wird eingekauft, wie steht der Abgleich. */
export function SettingsScreen() {
  const members = useLiveQuery(() => allMembers(), [], []);
  const stores = useLiveQuery(() => allStores(), [], []);
  const status = useLiveQuery(() => syncStatus(), [], { pending: 0, photos: 0, lastError: null, stuck: [] as HaengendeAenderung[] });
  const auskunft = useLiveQuery(() => selbstauskunft(), [], null);
  const [storeName, setStoreName] = useState("");
  const [chain, setChain] = useState<ChainKey | "">("");

  async function addStore(event: FormEvent) {
    event.preventDefault();
    const trimmed = storeName.trim();
    if (trimmed.length === 0) return;
    setStoreName("");
    await createStore(trimmed, chain === "" ? null : chain);
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-6 px-4 mit-leiste">
      <header className="flex items-center justify-between gap-3 pt-5">
        <h1 className="text-[2rem]">{de.settings.title}</h1>
        <SyncBadge />
      </header>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg">{de.settings.familySection}</h2>
        {!supabaseConfigured() ? (
          <p className="karte px-5 py-5 text-ink-2">{de.settings.localMode}</p>
        ) : members.length === 0 ? (
          <p className="text-ink-2">{de.common.loading}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {members.map((member) => (
              <li key={member.id} className="karte flex min-h-[56px] items-center justify-between px-4">
                <span className="truncate text-ink">{member.display_name}</span>
                <span className="text-sm text-ink-2">{member.role === "owner" ? "Verwaltung" : "Mitglied"}</span>
              </li>
            ))}
          </ul>
        )}
        {supabaseConfigured() && <InviteCard />}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg">{de.settings.storesSection}</h2>

        <form onSubmit={(event) => void addStore(event)} className="flex flex-col gap-2">
          <input
            value={storeName}
            onChange={(event) => {
              setStoreName(event.target.value);
            }}
            placeholder={de.stores.namePlaceholder}
            aria-label={de.stores.nameLabel}
            data-testid="store-name-input"
            className="karte min-h-[52px] px-4 text-ink outline-none placeholder:text-ink-3"
          />
          <select
            value={chain}
            onChange={(event) => {
              setChain(event.target.value as ChainKey | "");
            }}
            aria-label={de.stores.chainLabel}
            data-testid="store-chain"
            className="karte min-h-[52px] px-3 text-ink outline-none"
          >
            <option value="">{de.stores.noChain}</option>
            {CHAIN_PROFILES.map((profile) => (
              <option key={profile.key} value={profile.key}>
                {profile.name}
              </option>
            ))}
          </select>
          <p className="text-sm text-ink-2">{de.stores.chainHint}</p>
          <button
            type="submit"
            data-testid="store-create"
            className="knopf-haupt min-h-[52px] px-4"
          >
            {de.stores.create}
          </button>
        </form>

        {stores.length === 0 ? (
          <p className="text-ink-2">{de.stores.empty}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {stores.map((store) => (
              <li key={store.id} className="karte flex min-h-[56px] items-center gap-2 px-4">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ink" data-testid="store-row" data-name={store.name}>
                    {store.name}
                  </span>
                  <span className="block text-sm text-ink-2">
                    {store.chain ? CHAIN_PROFILES.find((p) => p.key === store.chain)?.name : de.stores.noChain}
                    {store.category_order.length > 0 ? ` · ${String(store.category_order.length)} Gänge` : ""}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => void deleteStore(store.id)}
                  aria-label={`${de.common.delete}: ${store.name}`}
                  className="inline-flex size-11 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-ink-3"
                >
                  <Trash2 size={18} strokeWidth={1.5} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {supabaseConfigured() && (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg">{de.spaisekammer.section}</h2>
          <SpaisekammerCard />
        </section>
      )}

      {supabaseConfigured() && <PushSchalter />}

      <section className="flex flex-col gap-3">
        <h2 className="text-lg">{de.settings.syncSection}</h2>

        <div className="karte flex flex-col gap-1 px-5 py-4" data-testid="sync-status">
          <span className={status.pending === 0 && status.photos === 0 ? "text-herb" : "text-ink"}>
            {status.pending === 0 ? de.settings.syncClean : de.settings.syncPending(status.pending)}
          </span>
          {status.photos > 0 && <span className="text-sm text-ink-2">{de.settings.syncPhotos(status.photos)}</span>}
          {status.lastError !== null && (
            <span className="text-sm text-accent">
              {de.settings.syncLastError}: {status.lastError}
            </span>
          )}
        </div>

        {/*
          Hängende Änderungen beim Namen nennen. Eine Zahl im Abzeichen sagt
          nur, dass etwas klemmt, nicht was und warum.
        */}
        {status.stuck.length > 0 && (
          <div className="karte kante flex flex-col gap-2 px-4 py-4" style={{ ["--kante-farbe" as string]: "var(--accent)" }} data-testid="sync-stuck">
            <p className="font-semibold text-ink">{de.settings.syncStuck}</p>
            <p className="text-sm text-ink-2">{de.settings.syncStuckHint}</p>
            <ul className="flex flex-col gap-2 pt-1">
              {status.stuck.map((eintrag) => (
                <li key={`${eintrag.table}-${eintrag.name}`} className="border-t border-paper-2 pt-2 text-sm first:border-0 first:pt-0">
                  <span className="block font-medium text-ink">
                    {eintrag.name} <span className="num text-ink-3">({de.settings.syncAttempts(eintrag.attempts)})</span>
                  </span>
                  <span className="block break-words text-ink-2">{eintrag.error}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {supabaseConfigured() && auskunft !== null && (
          <div className="karte flex flex-col gap-1.5 px-4 py-4 text-sm" data-testid="device-report">
            <p className="font-semibold text-ink">{de.settings.deviceSection}</p>
            <p className="num text-ink-2">
              {de.settings.deviceFamily}: <span className="break-all">{auskunft.aktiveFamilie}</span>
            </p>
            <p className="num text-ink-2">
              {de.settings.deviceLocal}: <span className="break-all">{auskunft.lokaleFamilie}</span>
            </p>
            <p className="num text-ink-2">{de.settings.deviceRows(auskunft.zeilen.listen, auskunft.zeilen.artikel)}</p>
            {auskunft.marken.length > 0 && (
              <p className="num text-ink-3">
                {de.settings.deviceMarks}: {auskunft.marken.map((marke) => `${marke.tabelle} ${marke.stand}`).join(" · ")}
              </p>
            )}

            <button
              type="button"
              onClick={() => {
                void alleNeuLaden().then(() => {
                  window.location.reload();
                });
              }}
              data-testid="sync-reload"
              className="knopf-leise mt-2 min-h-[48px] px-4 text-sm"
            >
              {de.settings.syncReload}
            </button>
          </div>
        )}

        {supabaseConfigured() && (
          <button
            type="button"
            onClick={() => void signOut()}
            data-testid="sign-out"
            className="knopf-leise min-h-[52px] px-4"
          >
            {de.auth.signOut}
          </button>
        )}
      </section>

      <BottomNav active="einstellungen" />
    </div>
  );
}
